// <neroli-slideshow> — accessible, single-item slideshow.
// Autoplay pauses on hover, focus-within, tab hidden, and prefers-reduced-motion.
class NeroliSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slideshow-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length === 0) return;

    this.prevButton = this.querySelector('[data-slideshow-prev]');
    this.nextButton = this.querySelector('[data-slideshow-next]');
    this.dots = Array.from(this.querySelectorAll('[data-slideshow-dot]'));

    this.index = 0;
    this.count = this.slides.length;
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this.timer = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onPrev = () => { this.stop(); this.go(this.index - 1); };
    this.onNext = () => { this.stop(); this.go(this.index + 1); };
    this.prevButton && this.prevButton.addEventListener('click', this.onPrev);
    this.nextButton && this.nextButton.addEventListener('click', this.onNext);

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { this.stop(); this.go(i); });
    });

    // Keyboard support on the region.
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { this.stop(); this.go(this.index - 1); }
      if (e.key === 'ArrowRight') { this.stop(); this.go(this.index + 1); }
    });

    // Pause conditions.
    if (this.count > 1 && this.autoplay && !this.reduceMotion) {
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());
      document.addEventListener('visibilitychange', this.onVisibility = () => {
        document.hidden ? this.pause() : this.play();
      });
      this.play();
    }

    // Basic touch swipe.
    if (this.count > 1) this.bindSwipe();

    this.update();
  }

  disconnectedCallback() {
    this.pause();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
  }

  go(i) {
    this.index = (i + this.count) % this.count;
    this.update();
  }

  update() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      // Keep focusable children out of tab order when off-screen.
      slide.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  play() {
    if (!this.autoplay || this.reduceMotion || this.count < 2 || this.timer) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  pause() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  // Permanent stop after user interaction — no surprise auto-advance.
  stop() {
    this.autoplay = false;
    this.pause();
  }

  bindSwipe() {
    let startX = 0;
    let dragging = false;
    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX; dragging = true;
    }, { passive: true });
    this.track.addEventListener('touchend', (e) => {
      if (!dragging) return;
      dragging = false;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 40) return;
      this.stop();
      this.go(dx < 0 ? this.index + 1 : this.index - 1);
    }, { passive: true });
  }
}

customElements.define('neroli-slideshow', NeroliSlideshow);
